import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CapacitorNfc, NdefRecord, NfcEvent, PluginListenerHandle } from '@capgo/capacitor-nfc';

type WebNdefRecordInit = {
  recordType: string;
  mediaType?: string;
  data?: unknown;
};

type WebNdefMessageInit = {
  records: WebNdefRecordInit[];
};

type WebNdefReader = {
  write(message: WebNdefMessageInit, options?: WebNdefWriteOptions): Promise<void>;
};

type WebNdefWindow = Window & {
  NDEFReader?: new () => WebNdefReader;
};

type WebNdefWriteOptions = {
  signal?: AbortSignal;
};

export type NfcWriterPayloadMode = 'url' | 'vcard';

export interface NfcWriterPayload {
  payload_mode: NfcWriterPayloadMode;
  payload: string;
}

export interface NfcMassWriteOptions {
  signal?: AbortSignal;
  onWrite?: (count: number) => void;
  lockAfterWrite?: boolean;
}

export interface NfcWriteOptions {
  signal?: AbortSignal;
  onWrite?: () => void;
  lockAfterWrite?: boolean;
}

type NativeWriteOptions = NfcWriteOptions & {
  ignoredTagId?: string;
  timeoutMs?: number | null;
};

const NFC_WRITE_TIMEOUT_MS = 60_000;
const TNF_WELL_KNOWN = 0x01;
const TNF_MIME_MEDIA = 0x02;
const ANDROID_READER_NFC_A = 0x01;
const ANDROID_READER_NFC_B = 0x02;
const ANDROID_READER_NFC_F = 0x04;
const ANDROID_READER_NFC_V = 0x08;
const ANDROID_READER_NO_PLATFORM_SOUNDS = 0x100;
const ANDROID_WRITE_READER_FLAGS = ANDROID_READER_NFC_A
  | ANDROID_READER_NFC_B
  | ANDROID_READER_NFC_F
  | ANDROID_READER_NFC_V
  | ANDROID_READER_NO_PLATFORM_SOUNDS;
const NFC_NATIVE_OPERATION_TIMEOUT_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class NfcWriterService {
  webAvailable(): boolean {
    return typeof window !== 'undefined'
      && window.isSecureContext
      && 'NDEFReader' in window
      && !Capacitor.isNativePlatform();
  }

  async canWrite(): Promise<boolean> {
    if (this.webAvailable()) {
      return true;
    }

    try {
      const { supported } = await CapacitorNfc.isSupported();
      if (!supported) {
        return false;
      }

      const { status } = await CapacitorNfc.getStatus();
      return status === 'NFC_OK';
    } catch {
      return false;
    }
  }

  async canBroadcast(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return false;
    }

    try {
      const { supported } = await CapacitorNfc.isSupported();
      if (!supported) {
        return false;
      }

      const { status } = await CapacitorNfc.getStatus();
      return status === 'NFC_OK';
    } catch {
      return false;
    }
  }

  async canLock(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return false;
    }

    try {
      const { supported } = await CapacitorNfc.isSupported();
      if (!supported) {
        return false;
      }

      const { status } = await CapacitorNfc.getStatus();
      return status === 'NFC_OK';
    } catch {
      return false;
    }
  }

  async writeUrl(url: string, options: NfcWriteOptions = {}): Promise<void> {
    const value = url.trim();
    if (!value) {
      throw new Error('Missing URL.');
    }

    if (options.lockAfterWrite && !(await this.canLock())) {
      throw new Error('Locking NFC tags is only available in the installed Android app.');
    }

    if (this.webAvailable()) {
      const ndef = this.webNdefReader();
      const message: WebNdefMessageInit = { records: [{ recordType: 'url', data: value }] };
      await ndef.write(message, { signal: options.signal });
      options.onWrite?.();
      return;
    }

    await this.writeNative([this.uriRecord(value)], options);
  }

  async sendTag(payload: NfcWriterPayload): Promise<void> {
    await this.broadcastPayload(payload);
  }

  async broadcastPayload(payload: NfcWriterPayload): Promise<void> {
    await this.broadcastRecords(this.recordsForPayload(payload));
  }

  async broadcastUrl(url: string): Promise<void> {
    const value = url.trim();
    if (!value) {
      throw new Error('Missing URL.');
    }

    await this.broadcastRecords([this.uriRecord(value)]);
  }

  async broadcastVcard(vcard: string): Promise<void> {
    const value = vcard.trim();
    if (!value) {
      throw new Error('Missing vCard payload.');
    }

    await this.broadcastRecords([this.mimeRecord('text/vcard', value)]);
  }

  async stopBroadcast(): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    await CapacitorNfc.unshare();
  }

  async writePayload(payload: NfcWriterPayload, options: NfcWriteOptions = {}): Promise<void> {
    if (payload.payload_mode === 'url') {
      await this.writeUrl(payload.payload, options);
      return;
    }

    if (payload.payload_mode === 'vcard') {
      await this.writeVcard(payload.payload, options);
      return;
    }

    throw new Error('Unsupported NFC payload type.');
  }

  async massWritePayload(payload: NfcWriterPayload, options: NfcMassWriteOptions = {}): Promise<void> {
    if (this.webAvailable()) {
      throw new Error('Mass writing requires the installed mobile app on a device with NFC enabled.');
    }

    await this.massWriteNative(this.recordsForPayload(payload), options);
  }

  async writeVcard(vcard: string, options: NfcWriteOptions = {}): Promise<void> {
    const value = vcard.trim();
    if (!value) {
      throw new Error('Missing vCard payload.');
    }

    if (options.lockAfterWrite && !(await this.canLock())) {
      throw new Error('Locking NFC tags is only available in the installed Android app.');
    }

    if (this.webAvailable()) {
      const ndef = this.webNdefReader();
      const message: WebNdefMessageInit = { records: [{ recordType: 'mime', mediaType: 'text/vcard', data: value }] };
      await ndef.write(message, { signal: options.signal });
      options.onWrite?.();
      return;
    }

    await this.writeNative([this.mimeRecord('text/vcard', value)], options);
  }

  private webNdefReader(): WebNdefReader {
    const Reader = (window as WebNdefWindow).NDEFReader;
    if (!Reader) {
      throw new Error('Web NFC is unavailable on this device.');
    }

    return new Reader();
  }

  private recordsForPayload(payload: NfcWriterPayload): NdefRecord[] {
    if (payload.payload_mode === 'url') {
      const value = payload.payload.trim();
      if (!value) {
        throw new Error('Missing URL.');
      }

      return [this.uriRecord(value)];
    }

    if (payload.payload_mode === 'vcard') {
      const value = payload.payload.trim();
      if (!value) {
        throw new Error('Missing vCard payload.');
      }

      return [this.mimeRecord('text/vcard', value)];
    }

    throw new Error('Unsupported NFC payload type.');
  }

  private async broadcastRecords(records: NdefRecord[]): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      throw new Error('NFC tag broadcasting is only available in the installed Android app.');
    }

    const { status } = await CapacitorNfc.getStatus();
    if (status === 'NFC_DISABLED') {
      throw new Error('NFC is disabled on this device.');
    }

    if (status === 'NDEF_PUSH_DISABLED') {
      throw new Error('NFC peer-to-peer sharing is disabled on this device.');
    }

    if (status !== 'NFC_OK') {
      throw new Error('NFC tag broadcasting is unavailable on this device.');
    }

    try {
      await CapacitorNfc.share({ records });
    } catch (error) {
      throw new Error(this.broadcastErrorMessage(error), { cause: error });
    }
  }

  private async writeNative(records: NdefRecord[], options: NativeWriteOptions = {}): Promise<NfcEvent | undefined> {
    const { status } = await CapacitorNfc.getStatus();
    if (status === 'NFC_DISABLED') {
      throw new Error('NFC is disabled on this device.');
    }

    if (status !== 'NFC_OK') {
      throw new Error('NFC writing is unavailable on this device.');
    }

    let listener: PluginListenerHandle | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let discoveredTag: NfcEvent | undefined;
    let rejectTagDiscovered: ((error: Error) => void) | undefined;

    const abortWrite = () => {
      rejectTagDiscovered?.(new Error('NFC write canceled.'));
      void CapacitorNfc.stopScanning().catch(() => undefined);
    };

    try {
      let resolveTagDiscovered: (() => void) | undefined;
      const tagDiscovered = new Promise<void>((resolve, reject) => {
        resolveTagDiscovered = resolve;
        rejectTagDiscovered = reject;
        const timeoutMs = options.timeoutMs === undefined ? NFC_WRITE_TIMEOUT_MS : options.timeoutMs;
        if (timeoutMs !== null) {
          timeoutId = setTimeout(() => {
            reject(new Error('Timed out waiting for an NFC tag.'));
          }, timeoutMs);
        }
      });

      if (options.signal?.aborted) {
        throw new Error('NFC write canceled.');
      }

      options.signal?.addEventListener('abort', abortWrite, { once: true });
      listener = await CapacitorNfc.addListener('nfcEvent', (event) => {
        if (options.signal?.aborted) {
          return;
        }

        if (options.ignoredTagId && this.tagId(event) === options.ignoredTagId) {
          return;
        }

        discoveredTag = event;
        resolveTagDiscovered?.();
      });

      await CapacitorNfc.startScanning({
        androidReaderModeFlags: ANDROID_WRITE_READER_FLAGS,
        invalidateAfterFirstRead: false,
        alertMessage: 'Hold a writable NFC tag near this device.'
      });
      await tagDiscovered;

      if (options.signal?.aborted) {
        throw new Error('NFC write canceled.');
      }

      if (options.lockAfterWrite && discoveredTag?.tag?.canMakeReadOnly === false) {
        throw new Error('This NFC tag cannot be locked. Use a tag that supports being made read-only, or turn off lock after write.');
      }

      try {
        await this.runNativeTagOperation(
          CapacitorNfc.write({ allowFormat: true, records }),
          'Timed out while writing the NFC tag. Move the tag away, tap Cancel Write if needed, and try again.'
        );
        if (options.signal?.aborted) {
          throw new Error('NFC write canceled.');
        }

        if (options.lockAfterWrite) {
          await this.lockNative(options.signal);
        }

        options.onWrite?.();
        return discoveredTag;
      } catch (error) {
        throw this.nativeWriteError(error, discoveredTag);
      }
    } finally {
      options.signal?.removeEventListener('abort', abortWrite);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      await listener?.remove();
      await CapacitorNfc.stopScanning().catch(() => undefined);
    }
  }

  private async massWriteNative(records: NdefRecord[], options: NfcMassWriteOptions): Promise<void> {
    const { status } = await CapacitorNfc.getStatus();
    if (status === 'NFC_DISABLED') {
      throw new Error('NFC is disabled on this device.');
    }

    if (status !== 'NFC_OK') {
      throw new Error('NFC writing is unavailable on this device.');
    }

    let writeCount = 0;
    let lastWrittenTagId: string | undefined;

    while (!options.signal?.aborted) {
      const writtenTag = await this.writeNative(records, {
        signal: options.signal,
        lockAfterWrite: options.lockAfterWrite,
        ignoredTagId: lastWrittenTagId,
        timeoutMs: null,
        onWrite: () => {
          writeCount += 1;
          options.onWrite?.(writeCount);
        }
      }).catch((error) => {
        if (options.signal?.aborted) {
          return undefined;
        }

        throw error;
      });

      lastWrittenTagId = writtenTag ? this.tagId(writtenTag) : undefined;
    }
  }

  private async lockNative(signal?: AbortSignal): Promise<void> {
    if (!(await this.canLock())) {
      throw new Error('Locking NFC tags is only available in the installed Android app.');
    }

    if (signal?.aborted) {
      throw new Error('NFC write canceled.');
    }

    await this.runNativeTagOperation(
      CapacitorNfc.makeReadOnly(),
      'NFC tag was written, but locking timed out. Move the tag away, then verify it before using it.'
    );
  }

  private nativeWriteError(error: unknown, event: NfcEvent | undefined): Error {
    const message = this.errorMessage(error);
    if (this.isLockedTagError(message, event)) {
      return new Error('This NFC tag is locked and cannot be written.');
    }

    if (!message.includes('Tag does not support NDEF')) {
      return new Error(message);
    }

    const details = this.tagDetails(event);
    return new Error([
      'This tag does not report NDEF writing or formatting support.',
      'Try a writable NFC Forum tag such as NTAG213/215/216, or format the tag as NDEF with another NFC tool first.',
      details ? `Detected tag details: ${details}.` : ''
    ].filter(Boolean).join(' '));
  }

  private isLockedTagError(message: string, event: NfcEvent | undefined): boolean {
    const normalizedMessage = message.toLowerCase();
    return event?.tag?.isWritable === false
      || normalizedMessage.includes('read only')
      || normalizedMessage.includes('read-only');
  }

  private tagDetails(event: NfcEvent | undefined): string {
    const parts = [
      event?.type ? `event=${event.type}` : '',
      event?.tag?.type ? `type=${event.tag.type}` : '',
      event?.tag?.techTypes?.length ? `tech=${event.tag.techTypes.join(', ')}` : ''
    ];

    return parts.filter(Boolean).join('; ');
  }

  private tagId(event: NfcEvent | undefined): string | undefined {
    const id = event?.tag?.id;
    return id?.length ? id.join('-') : undefined;
  }

  private async runNativeTagOperation<T>(operation: Promise<T>, timeoutMessage: string): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        reject(new Error(timeoutMessage));
      }, NFC_NATIVE_OPERATION_TIMEOUT_MS);
    });

    try {
      return await Promise.race([operation, timeout]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (timedOut) {
        operation.catch(() => undefined);
      }
    }
  }

  private errorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }

    return 'Unable to write NFC tag.';
  }

  private broadcastErrorMessage(error: unknown): string {
    const message = this.errorMessage(error);

    if (message.includes('Android 10 or later')) {
      return 'NFC peer-to-peer broadcasting is not supported on Android 10 or later.';
    }

    if (message.includes('iOS')) {
      return 'NFC peer-to-peer broadcasting is not available on iOS.';
    }

    return message === 'Unable to write NFC tag.' ? 'Unable to broadcast NFC tag.' : message;
  }

  private uriRecord(value: string): NdefRecord {
    return {
      tnf: TNF_WELL_KNOWN,
      type: this.bytes('U'),
      id: [],
      payload: [0x00, ...this.bytes(value)]
    };
  }

  private mimeRecord(mediaType: string, value: string): NdefRecord {
    return {
      tnf: TNF_MIME_MEDIA,
      type: this.bytes(mediaType),
      id: [],
      payload: this.bytes(value)
    };
  }

  private bytes(value: string): number[] {
    return Array.from(new TextEncoder().encode(value));
  }
}
